import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Loader2, Plug, CheckCircle2, ArrowRight, Sparkles, HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ModuleGuide } from "@/components/ModuleGuide";
import { INTEGRATIONS_GUIDE } from "@/lib/guides";

const CATEGORY_ORDER = ["CRM", "Email & Calendar", "Communication", "Productivity", "Commerce"];

const STATUS_META = {
  connected:     { label: "Connected",         cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" },
  mocked:        { label: "Connected (mocked)", cls: "border-amber-500/30 bg-amber-500/10 text-amber-200" },
  not_connected: { label: "Not connected",     cls: "border-white/15 bg-white/5 text-white/65" },
  coming_soon:   { label: "Coming soon",       cls: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-200" },
};

export default function Integrations() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [tab, setTab] = useState("catalog");

  const load = () => {
    setLoading(true);
    api.get("/integrations").then((r) => setItems(r.data)).catch((e) => toast.error(errorMessage(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const byCategory = items.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="mx-auto max-w-7xl">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="text-eyebrow text-rose-400" data-testid="integrations-eyebrow">Integrations</div>
        <h1 className="font-display mt-2 text-4xl font-light tracking-tight sm:text-5xl">
          Connect the rest of your <span className="brand-text">stack.</span>
        </h1>
        <p className="mt-2 max-w-xl text-white/55">
          16 integrations across CRM, email, communication, productivity and commerce. Add your name to the waitlist
          for early access — we ship them in order of demand.
        </p>
      </motion.div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-white/5 text-white" data-testid="int-tabs">
          <TabsTrigger value="catalog" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="int-tab-catalog">
            <Plug className="mr-1 h-3.5 w-3.5" /> Catalog
          </TabsTrigger>
          <TabsTrigger value="guide" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="int-tab-guide">
            <HelpCircle className="mr-1 h-3.5 w-3.5" /> Guide
          </TabsTrigger>
        </TabsList>

        <TabsContent value="catalog" className="mt-2">
      {loading ? (
        <div className="flex items-center justify-center py-20 text-white/45"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="mt-8 space-y-8" data-testid="integrations-page">
          {/* Iter 21 — "Available now" section pinned at the top. Surfaces
              live integrations (status=connected or mocked) ahead of the
              category grid so the honesty signal isn't buried inside
              alphabetised buckets. Independent-tester flag (post-iter-20). */}
          {items.some((it) => it.status === "connected" || it.status === "mocked") && (
            <section data-testid="int-available-now">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
                <div className="text-eyebrow text-emerald-300">Available now</div>
              </div>
              <p className="mt-1 text-xs text-white/45">Live integrations you can wire up today. The rest are on the waitlist below — order of build = order of demand.</p>
              <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {items
                  .filter((it) => it.status === "connected" || it.status === "mocked")
                  .map((it) => (
                    <Card key={`live-${it.provider}`} item={it} onConnect={() => setOpen(it)} />
                  ))}
              </div>
            </section>
          )}

          <div className="border-t border-white/5 pt-6">
            <div className="text-eyebrow text-fuchsia-300">Coming soon — by category</div>
            <p className="mt-1 text-xs text-white/45">Tell us what to build next by joining a waitlist below.</p>
          </div>

          {CATEGORY_ORDER.filter((c) => byCategory[c]?.length).map((cat) => {
            const comingInCat = byCategory[cat].filter((it) => it.status !== "connected" && it.status !== "mocked");
            if (comingInCat.length === 0) return null;
            return (
              <section key={cat}>
                <div className="text-eyebrow text-white/40">{cat}</div>
                <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {comingInCat.map((it) => (
                    <Card key={it.provider} item={it} onConnect={() => setOpen(it)} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
        </TabsContent>
        <TabsContent value="guide" className="mt-4">
          <ModuleGuide
            title={INTEGRATIONS_GUIDE.title}
            whatItIs={INTEGRATIONS_GUIDE.whatItIs}
            capabilities={INTEGRATIONS_GUIDE.capabilities}
            howTo={INTEGRATIONS_GUIDE.howTo}
            ctaLabel={INTEGRATIONS_GUIDE.cta}
            onCta={() => setTab("catalog")}
          />
        </TabsContent>
      </Tabs>

      <WaitlistDialog
        open={!!open} onOpenChange={(o) => !o && setOpen(null)}
        item={open} defaultEmail={user?.email}
        onSubmitted={() => { setOpen(null); load(); }}
      />
    </div>
  );
}

function Card({ item, onConnect }) {
  const meta = STATUS_META[item.status];
  const isMocked = item.status === "mocked";
  const isComing = item.status === "coming_soon";
  return (
    <div className="glass glass-hover rounded-2xl p-5" data-testid={`integration-card-${item.provider}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5">
            <Plug className="h-4 w-4 text-white/85" />
          </div>
          <div>
            <div className="font-display text-base font-medium text-white">{item.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-white/40">{item.category}</div>
          </div>
        </div>
        <span className={`flex-shrink-0 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${meta.cls}`}>{meta.label}</span>
      </div>
      <p className="mt-3 text-xs text-white/55">{item.description}</p>
      <div className="mt-4">
        {isMocked || item.status === "connected" ? (
          <Button disabled variant="outline" className="w-full border-white/15 bg-white/5 text-white/85" data-testid={`int-${item.provider}-connected`}>
            <CheckCircle2 className="mr-1.5 h-4 w-4 text-emerald-300" /> Connected
          </Button>
        ) : item.waitlisted ? (
          <Button disabled variant="outline" className="w-full border-emerald-500/40 bg-emerald-500/10 text-emerald-300" data-testid={`int-${item.provider}-waitlisted`}>
            <CheckCircle2 className="mr-1.5 h-4 w-4" /> You're on the waitlist
          </Button>
        ) : isComing ? (
          // Iter 20 (audit Finding 21 / P0): show a clearly distinct "Join
          // waitlist" CTA for non-live providers — never a deceptive
          // "Connect" button that secretly opens a waitlist dialog.
          <Button
            onClick={onConnect}
            variant="outline"
            className="w-full rounded-xl border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-200 hover:bg-fuchsia-500/15"
            data-testid={`int-${item.provider}-waitlist`}
          >
            Join waitlist <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        ) : (
          <Button onClick={onConnect} className="w-full rounded-xl bg-white text-black hover:bg-zinc-200" data-testid={`int-${item.provider}-connect`}>
            Connect <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function WaitlistDialog({ open, onOpenChange, item, defaultEmail, onSubmitted }) {
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!item) return;
    setSaving(true);
    try {
      // Iter 20 — backend ignores the `email` field now (uses auth user)
      // but we keep sending it for backwards-compat with older payloads.
      await api.post(`/integrations/${item.provider}/waitlist`, {});
      toast.success("You're on the waitlist");
      onSubmitted();
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#0a0a0a] text-white sm:max-w-md" data-testid="int-waitlist-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-2xl"><Sparkles className="h-5 w-5 text-fuchsia-300" /> {item?.name}</DialogTitle>
          <DialogDescription className="text-white/55">
            We haven't shipped this one yet — but we ship in order of demand. Joining the waitlist
            tells us which integrations to build next. We'll email <span className="text-white">{defaultEmail || "you"}</span> when it's live.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-white/65">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="int-waitlist-submit">
              {saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} Join waitlist
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
